import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase/config';
import { collection, addDoc, getDocs, deleteDoc, doc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import Swal from 'sweetalert2';
import './Documents.css';

const Documents = () => {
    const { isAdmin, currentUser, userData } = useAuth();
    const { showToast } = useToast();
    const [documents, setDocuments] = useState([]);
    const [selectedPdf, setSelectedPdf] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(true);

    // Cloudinary configuration
    const CLOUDINARY_CLOUD_NAME = 'dblffn98e';
    const CLOUDINARY_UPLOAD_PRESET = 'white_doves_ministry';

    useEffect(() => {
        loadDocuments();
    }, []);

    const loadDocuments = async () => {
        try {
            const documentsRef = collection(db, 'documents');
            const q = query(documentsRef, orderBy('uploadedAt', 'desc'));
            const snapshot = await getDocs(q);
            
            const docsList = [];
            snapshot.forEach(doc => {
                docsList.push({
                    id: doc.id,
                    ...doc.data()
                });
            });
            
            setDocuments(docsList);
        } catch (error) {
            console.error('Error loading documents:', error);
            showToast('Error loading documents', 'error');
        } finally {
            setLoading(false);
        }
    };

   const uploadToCloudinary = async (file, progressCallback) => {
    return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', 'white_doves_ministry');
        formData.append('resource_type', 'auto'); 

        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
                const percent = Math.round((e.loaded / e.total) * 100);
                if (progressCallback) progressCallback(percent);
            }
        });

        xhr.onload = () => {
            if (xhr.status === 200) {
                resolve(JSON.parse(xhr.responseText));
            } else {
                console.error(xhr.responseText); // 
                reject(new Error('Upload failed'));
            }
        };

        xhr.onerror = () => reject(new Error('Network error'));

        xhr.open('POST', `https://api.cloudinary.com/v1_1/dblffn98e/auto/upload`);
        xhr.send(formData);
    });
};

    const handleUploadClick = () => {
        if (!isAdmin) {
            showToast('Only administrators can upload documents', 'error');
            return;
        }
        document.getElementById('fileInput').click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (file.type !== 'application/pdf') {
            showToast('Only PDF files are accepted', 'error');
            return;
        }

        // Validate file size (20MB max for Cloudinary free tier)
        if (file.size > 20 * 1024 * 1024) {
            showToast('File size must be less than 20MB', 'error');
            return;
        }

        setUploading(true);

        try {
            // Upload to Cloudinary with progress
            const result = await uploadToCloudinary(file, (progress) => {
                // Update progress if needed
                console.log(`Upload progress: ${progress}%`);
            });

            // Save to Firestore
           const docData = {
    name: file.name || 'Unnamed',
    url: result.url || '',
    publicId: result.publicId || '', // ✅ FIXED
    size: formatFileSize(result.bytes || 0),
    fileSize: result.bytes || 0,
    format: result.format || 'pdf',
    uploadedAt: serverTimestamp(),
    uploadedBy: currentUser?.uid || null,
    uploadedByName: userData?.fullName || currentUser?.email || 'Unknown'
};

            const docRef = await addDoc(collection(db, 'documents'), docData);
            
            // Add to local state
            setDocuments(prev => [{
                id: docRef.id,
                ...docData,
                uploadedAt: new Date().toISOString()
            }, ...prev]);

            showToast('Document uploaded successfully!', 'success');
            
            // Clear file input
            e.target.value = '';
            
        } catch (error) {
            console.error('Error uploading document:', error);
            showToast('Failed to upload document: ' + error.message, 'error');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteDocument = async (docId, docName) => {
        if (!isAdmin) {
            showToast('Only administrators can delete documents', 'error');
            return;
        }

        const result = await Swal.fire({
            title: 'Delete Document?',
            text: `Are you sure you want to delete "${docName}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, 'documents', docId));
                setDocuments(prev => prev.filter(doc => doc.id !== docId));
                showToast('Document deleted successfully!', 'success');
            } catch (error) {
                console.error('Error deleting document:', error);
                showToast('Failed to delete document', 'error');
            }
        }
    };

    const handleViewDocument = (url) => {
        setSelectedPdf(url);
        setShowModal(true);
    };

    const handleDownloadDocument = (doc) => {
        window.open(doc.url, '_blank');
        showToast(`Opening ${doc.name}`, 'info');
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return 'Invalid date';
        }
    };

    if (loading) {
        return (
            <div className="text-center" style={{ padding: '50px' }}>
                <i className="fas fa-spinner fa-spin fa-3x" style={{ color: '#1034A6' }}></i>
                <p style={{ marginTop: '20px' }}>Loading documents...</p>
            </div>
        );
    }

    return (
        <div className="documents-page">
            <div className="documents-main-content">
                <h1 className="documents-page-title">Ministry Documents</h1>

                {/* Upload Section */}
                <div className="documents-upload-section">
                    <div className="documents-upload-header">
                        <h2 className="documents-upload-title">Upload Documents</h2>
                        <button 
                            className="documents-upload-btn" 
                            onClick={handleUploadClick}
                            disabled={uploading || !isAdmin}
                        >
                            {uploading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i> Uploading...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-cloud-upload-alt"></i> Upload PDF
                                </>
                            )}
                        </button>
                        <input 
                            type="file" 
                            id="fileInput" 
                            className="documents-file-input" 
                            accept=".pdf"
                            onChange={handleFileChange}
                        />
                    </div>
                    <p className="documents-file-info">
                        Only PDF files are accepted. Maximum file size: 20MB
                    </p>
                    {!isAdmin && (
                        <p className="documents-file-info documents-info-warning">
                            <i className="fas fa-info-circle"></i> 
                            Only administrators can upload documents
                        </p>
                    )}
                </div>

                {/* Documents List */}
                <div className="documents-container">
                    {documents.length === 0 ? (
                        <div className="documents-empty-state">
                            <i className="fas fa-file-pdf"></i>
                            <h3>No Documents Found</h3>
                            <p>Upload your first document to get started</p>
                        </div>
                    ) : (
                        documents.map((doc) => (
                            <div key={doc.id} className="documents-card">
                                <div className="documents-preview">
                                    <i className="fas fa-file-pdf documents-pdf-icon"></i>
                                    <span className="documents-file-size">{doc.size}</span>
                                </div>
                                <div className="documents-content">
                                    <h3 className="documents-title">{doc.name}</h3>
                                    <div className="documents-details">
                                        <span>
                                            <i className="far fa-calendar-alt"></i> 
                                            Uploaded: {formatDate(doc.uploadedAt)}
                                        </span>
                                        <span>
                                            <i className="far fa-user"></i> 
                                            By: {doc.uploadedByName || 'Unknown'}
                                        </span>
                                    </div>
                                    <div className="documents-actions">
                                        <button 
                                            className="documents-action-btn documents-view-btn"
                                            onClick={() => handleViewDocument(doc.url)}
                                        >
                                            <i className="fas fa-eye"></i> View
                                        </button>
                                        <button 
                                            className="documents-action-btn documents-download-btn"
                                            onClick={() => handleDownloadDocument(doc)}
                                        >
                                            <i className="fas fa-download"></i> Download
                                        </button>
                                        {isAdmin && (
                                            <button 
                                                className="documents-action-btn documents-delete-btn"
                                                onClick={() => handleDeleteDocument(doc.id, doc.name)}
                                            >
                                                <i className="fas fa-trash"></i> Delete
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* PDF Viewer Modal */}
            {showModal && selectedPdf && (
                <div className="documents-modal" onClick={() => setShowModal(false)}>
                    <div className="documents-modal-content" onClick={e => e.stopPropagation()}>
                        <button className="documents-close-modal" onClick={() => setShowModal(false)}>
                            &times;
                        </button>
                        <div className="documents-pdf-toolbar">
                            <button 
                                className="documents-pdf-toolbar-btn"
                                onClick={() => window.open(selectedPdf, '_blank')}
                            >
                                <i className="fas fa-external-link-alt"></i> Open in New Tab
                            </button>
                            <button 
                                className="documents-pdf-toolbar-btn"
                                onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = selectedPdf;
                                    link.download = 'document.pdf';
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                }}
                            >
                                <i className="fas fa-download"></i> Download
                            </button>
                        </div>
                        <iframe 
                            className="documents-pdf-viewer" 
                            src={selectedPdf}
                            title="PDF Viewer"
                        ></iframe>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Documents;
